/**
 * The area-specific part of two tool checks' pages (Quality / Code scanning, Quality / Lighthouse), above the findings.
 * Code scanning: the tools that ran (Semgrep for C#, Rust, PHP and Ruby; Hadolint for the Dockerfiles), the languages
 * and each service. Lighthouse: per web page its four scores (0-100, coloured as Lighthouse does) and the key metrics
 * (LCP, TBT, CLS, FCP). External tools have their own part (QualityExternal).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Pill, StatusPill } from './QualityCommon';
import './QualityTools.css';
import DataTable from '../table/DataTable';
import {
  LIGHTHOUSE_CATEGORIES, languageName, lighthouseMetrics, lighthouseTone, plural, toolStateOf,
} from './qualityModel';

const TOOL_COLUMNS = [
  { key: 'name', label: 'Tool', width: 180, value: (t) => t.name },
  { key: 'version', label: 'Version', width: 140, className: 'quality-mono quality-small', value: (t) => t.version || '–' },
  {
    key: 'status',
    label: 'Status',
    wrap: 2,
    value: (t) => toolStateOf(t.state).label,
    title: (t) => [toolStateOf(t.state).label, t.message].filter(Boolean).join(': '),
    render: (t) => { const state = toolStateOf(t.state); return <><Pill tone={state.tone}>{state.label}</Pill>{t.message ? <span className="quality-small"> {t.message}</span> : null}</>; },
  },
];

const SCANNED_COLUMNS = [
  { key: 'service', label: 'Service', width: 180, className: 'quality-mono', value: (s) => s.service },
  { key: 'languages', label: 'Languages', width: 200, value: (s) => (s.languages || []).map(languageName).join(', ') || '–' },
  { key: 'status', label: 'Status', wrap: 2, value: (s) => s.status, title: (s) => s.message, render: (s) => <><StatusPill status={s.status} />{s.message ? <span className="quality-small"> {s.message}</span> : null}</> },
];

// A page that was not checked says why in its first score's cell (the others show –).
const LIGHTHOUSE_COLUMNS = [
  {
    key: 'page',
    label: 'Page',
    wrap: 2,
    value: (p) => p.service,
    title: (p) => [p.service, p.url].filter(Boolean).join(' · '),
    render: (p) => <><span className="quality-mono">{p.service}</span>{p.url && <><br /><span className="quality-muted quality-mono quality-small">{p.url}</span></>}</>,
  },
  ...LIGHTHOUSE_CATEGORIES.map((c, index) => ({
    key: c.key,
    label: c.label,
    align: 'center',
    width: index === 0 ? 130 : 110,
    wrap: index === 0 ? 3 : 1,
    value: (p) => (p.status === 'done' ? p.scores?.[c.key] : null),
    title: (p) => (p.status !== 'done' && index === 0 ? p.message : undefined),
    render: (p) => {
      if (p.status === 'done') { return <LighthouseScore label={c.label} score={p.scores?.[c.key]} />; }
      if (index > 0) { return '–'; }
      return <><Pill tone="warn">{p.status === 'timeout' ? 'Too slow' : 'Not checked'}</Pill>{p.message ? <span className="quality-small"> {p.message}</span> : null}</>;
    },
  })),
  {
    key: 'metrics',
    label: 'Key metrics',
    width: 220,
    wrap: 2,
    className: 'quality-small',
    headerTitle: 'Key metrics (sorts on LCP, the time until the main content shows)',
    value: (p) => (p.status === 'done' ? p.metrics?.lcpMs : null),
    render: (p) => (p.status === 'done' ? lighthouseMetrics(p.metrics).map((m) => (
      <span className="quality-lh-metric" key={m.key} title={m.label}><abbr title={m.label}>{m.short}</abbr> {m.value} </span>
    )) : '–'),
  },
];

export function CodeScanning({ check }) {
  const d = check.details || {};
  // Not needed: the check's summary says why, once; no tool or service table repeats it.
  if (check.status === 'not_needed') { return null; }
  const tools = d.tools || [];
  const services = (check.services || []).filter((s) => s.status !== 'not_needed' && (s.languages?.length || s.status !== 'pass' || s.message));
  const scanned = [(d.languages || []).length ? `Languages scanned: ${d.languages.map(languageName).join(', ')}` : null, d.dockerfiles ? plural(d.dockerfiles, 'Dockerfile') : null].filter(Boolean);
  return (
    <section aria-label="Code scanning" className="quality-area" data-testid="quality-code-scanning">
      {scanned.length > 0 && <p className="quality-muted quality-small">{scanned.join(' · ')}</p>}
      {tools.length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Tools"
            className="quality-table"
            columns={TOOL_COLUMNS}
            rowKey="name"
            rowProps={(t) => ({ 'data-testid': `quality-tool-${t.name}` })}
            rows={tools}
          />
        </div>
      )}
      {services.length > 0 && (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Services scanned"
            className="quality-table"
            columns={SCANNED_COLUMNS}
            rowKey="service"
            rows={services}
          />
        </div>
      )}
    </section>
  );
}

CodeScanning.propTypes = { check: PropTypes.object.isRequired };

/** A Lighthouse score (0-100) in a pill coloured as Lighthouse does; '–' when the page gave none. */
export function LighthouseScore({ score, label }) {
  if (score === null || score === undefined) { return <span aria-label={`${label}: no score`}>–</span>; }
  return <Pill aria-label={`${label}: ${score} out of 100`} className="quality-score" tone={lighthouseTone(score)}>{score}</Pill>;
}

LighthouseScore.propTypes = { label: PropTypes.string.isRequired, score: PropTypes.number };

export function Lighthouse({ check }) {
  const d = check.details || {};
  const pages = d.pages || [];
  return (
    <section aria-label="Lighthouse" className="quality-area" data-testid="quality-lighthouse">
      <p className="quality-muted quality-small">
        Each running web page, in a headless browser{d.standard ? ` (${d.standard})` : ''}. Scores go from 0 to 100: 90 and over is good, under 50 is poor.
      </p>
      {pages.length === 0 ? <p className="quality-muted quality-small">No page checked yet: start a web service&apos;s preview, then run Lighthouse.</p> : (
        <div className="quality-card quality-table-box">
          <DataTable
            ariaLabel="Lighthouse scores"
            className="quality-table"
            columns={LIGHTHOUSE_COLUMNS}
            rowKey={(p) => `${p.service}:${p.url}`}
            rowProps={(p) => ({ 'data-testid': `quality-lighthouse-${p.service}` })}
            rows={pages}
            storageKey="flow.quality.lighthouse.hiddenColumns"
          />
        </div>
      )}
    </section>
  );
}

Lighthouse.propTypes = { check: PropTypes.object.isRequired };
